import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useToast } from '../../../shared/components/toast/useToast';
import { useMerchant } from '../../merchants/hooks/useMerchants';
import {
  useAnalyticsConfig,
  useApproveReport,
  useIntelligenceReport,
  useReleaseReport,
} from '../hooks/useIntelligenceReports';
import { intelligenceReportsApi } from '../api/intelligenceReportsApi';
import { ReportMarkdown } from '../markdown';
import { SourceProvenancePanel } from '../components/SourceProvenancePanel';
import { AnalyticsConfigModal } from '../components/AnalyticsConfigModal';
import { formatPeriod } from '../period';
import { draftVersion, displayVersion, hasEditedVersion } from '../provenance';
import {
  GENERATION_METHOD_LABELS,
  STATUS_HINTS,
  STATUS_LABELS,
  STATUS_STYLES,
} from '../types';

const fmtDateTime = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/**
 * Review and release one monthly intelligence report (PE-2141).
 *
 * The lifecycle here is the backend's, not the ticket prose's: approve and
 * release are two distinct transitions, and APPROVED is still invisible to the
 * merchant. The UI makes that explicit at every step because "approved" reading
 * as "sent" is the expensive mistake this screen has to prevent.
 *
 * Editing is only possible while PENDING_REVIEW — the backend accepts edited
 * `content` on the approve call alone. There is no edit endpoint for an already
 * approved report, so the editor is hidden once the report leaves that state
 * rather than offering a control that would 400.
 */
export function IntelligenceReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.INTELLIGENCE_REPORT_MANAGE);

  const { data: report, isLoading, error } = useIntelligenceReport(id);
  const { data: merchant } = useMerchant(report?.merchantId ?? '');
  const { data: config, isSuccess: configLoaded } = useAnalyticsConfig(report?.merchantId);

  const approve = useApproveReport();
  const release = useReleaseReport();

  const draft = useMemo(() => draftVersion(report), [report]);
  const shown = useMemo(() => displayVersion(report), [report]);
  const edited = useMemo(() => hasEditedVersion(report), [report]);

  const [isEditing, setIsEditing] = useState(false);
  const [editorValue, setEditorValue] = useState('');
  const [configOpen, setConfigOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (shown) setEditorValue(shown.content);
  }, [shown]);

  const isPending = report?.status === 'PENDING_REVIEW';
  const canExport = report?.status === 'APPROVED' || report?.status === 'RELEASED';
  const contentDirty = !!shown && editorValue.trim() !== shown.content.trim();

  const handleApprove = async () => {
    if (!id) return;
    // Only send `content` when it actually differs — an unchanged send would
    // mint a redundant RELEASED version row and make the audit trail imply an
    // edit that never happened.
    await approve.mutateAsync({ id, content: contentDirty ? editorValue : undefined });
    setIsEditing(false);
  };

  const handleExport = async () => {
    if (!report) return;
    setExporting(true);
    try {
      await intelligenceReportsApi.exportPdf(
        report.id,
        `intelligence-report-${report.merchantId}-${report.period}.pdf`,
      );
      toast.success('Report downloaded');
    } catch (err) {
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error('Export failed. Please check your connection and try again.');
      }
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) {
    return <div className="py-16 text-center text-muted-foreground">Loading report…</div>;
  }

  if (error || !report) {
    return (
      <div className="py-16 text-center">
        <p className="text-red-500">This intelligence report could not be loaded.</p>
        <Button variant="outline" size="sm" className="h-[40px] mt-4 mx-auto" onClick={() => navigate('/intelligence-reports')}>
          Back to reports
        </Button>
      </div>
    );
  }

  return (
    <div className="py-6 max-w-5xl mx-auto px-2">
      <button
        onClick={() => navigate('/intelligence-reports')}
        className="text-xs text-blue-600 hover:underline"
      >
        ← All intelligence reports
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mt-3 mb-6">
        <div>
          <h1 className="text-[23px] font-bold text-foreground">
            {formatPeriod(report.period)} intelligence report
          </h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            {merchant?.name ?? <span className="font-mono text-xs">{report.merchantId}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canExport && (
            <Button
              variant="outline"
              size="sm"
              className="h-[40px]"
              onClick={handleExport}
              loading={exporting}
            >
              Export PDF
            </Button>
          )}
          {canManage && isPending && (
            <Button size="sm" className="h-[40px]" loading={approve.isPending} onClick={handleApprove}>
              {contentDirty ? 'Save edits & approve' : 'Approve'}
            </Button>
          )}
          {canManage && report.status === 'APPROVED' && (
            <Button
              size="sm"
              className="h-[40px]"
              loading={release.isPending}
              onClick={() => id && release.mutate(id)}
            >
              Release to merchant
            </Button>
          )}
        </div>
      </div>

      {/* ---- Status ---- */}
      <div className="bg-card rounded-2xl border border-border p-5 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-[11px] font-[600] px-2 py-0.5 rounded-full ${STATUS_STYLES[report.status]}`}>
            {STATUS_LABELS[report.status]}
          </span>
          <span className="text-[13px] text-muted-foreground">{STATUS_HINTS[report.status]}</span>
        </div>

        <dl className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-3">
          <div>
            <dt className="text-[12px] text-muted-foreground">Generated</dt>
            <dd className="text-[13px] text-foreground">{fmtDateTime(report.generatedAt)}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-muted-foreground">Approved</dt>
            <dd className="text-[13px] text-foreground">{fmtDateTime(report.approvedAt)}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-muted-foreground">Released</dt>
            <dd className="text-[13px] text-foreground">{fmtDateTime(report.releasedAt)}</dd>
          </div>
        </dl>

        {draft && (
          <p className="mt-4 text-[12px] text-muted-foreground">
            Drafted by{' '}
            <span className="font-medium text-foreground">
              {GENERATION_METHOD_LABELS[draft.generationMethod]}
            </span>
            {draft.generationMethod === 'RULE_BASED' &&
              ' — written from fixed thresholds rather than a language model.'}
            {edited && ' The released copy has been edited during review; the original draft is retained below.'}
          </p>
        )}
      </div>

      {/* ---- Provenance ---- */}
      <div className="mb-6">
        <SourceProvenancePanel
          rawData={draft?.rawData}
          config={config}
          configLoaded={configLoaded}
          onConfigure={() => setConfigOpen(true)}
        />
      </div>

      {/* ---- Report content ---- */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-[15px] font-semibold text-foreground">Report</h2>
            <p className="text-[13px] text-muted-foreground mt-1">
              {isPending
                ? 'Review the draft below. Edits are saved when you approve.'
                : 'This is the copy the merchant sees once released.'}
            </p>
          </div>
          {canManage && isPending && (
            <button
              type="button"
              onClick={() => setIsEditing((v) => !v)}
              className="shrink-0 text-xs text-blue-600 hover:underline"
            >
              {isEditing ? 'Preview' : 'Edit'}
            </button>
          )}
        </div>

        {!shown ? (
          <p className="py-8 text-center text-[13px] text-muted-foreground">
            This report has no content version.
          </p>
        ) : isEditing ? (
          <>
            <textarea
              value={editorValue}
              onChange={(e) => setEditorValue(e.target.value)}
              spellCheck
              className="w-full min-h-[420px] rounded-[10px] border border-border bg-background p-4 font-mono text-[13px] leading-relaxed text-foreground focus:outline-none"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[12px] text-muted-foreground">
                Markdown. The original AI draft is always retained for audit.
              </span>
              {contentDirty && (
                <button
                  type="button"
                  onClick={() => setEditorValue(shown.content)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  Revert edits
                </button>
              )}
            </div>
          </>
        ) : (
          <ReportMarkdown content={contentDirty ? editorValue : shown.content} />
        )}
      </div>

      {/* ---- Audit trail: the original draft, once it differs from the released copy ---- */}
      {edited && draft && (
        <details className="mt-6 bg-card rounded-2xl border border-border p-5">
          <summary className="cursor-pointer text-[15px] font-semibold text-foreground">
            Original AI draft
          </summary>
          <p className="text-[13px] text-muted-foreground mt-1 mb-4">
            Retained unchanged for audit — generated {fmtDateTime(draft.createdAt)}.
          </p>
          <ReportMarkdown content={draft.content} />
        </details>
      )}

      {report.merchantId && (
        <AnalyticsConfigModal
          isOpen={configOpen}
          onClose={() => setConfigOpen(false)}
          merchantId={report.merchantId}
          merchantName={merchant?.name}
          canManage={canManage}
        />
      )}
    </div>
  );
}
